import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { request } from '@/lib/api.js';
import {
  Chip,
  EmptyState,
  GlassButton,
  LoadingState,
  MetricCard,
  PageHeader,
  Pagination,
} from '@/components/ui/index.js';
import { useFmt } from '@/lib/useFmt.js';
import type { VisitorListItem, VisitorListResponse } from '@/lib/types.js';

const PAGE_SIZE = 50;

type StatusFilter = 'all' | 'unconverted' | 'converted';
type RecentFilter = 'all' | '7d' | '30d';

const STATUS_LABEL: Record<StatusFilter, string> = {
  all: '全部',
  unconverted: '未转化',
  converted: '已转化',
};

const RECENT_LABEL: Record<RecentFilter, string> = {
  all: '全部时间',
  '7d': '近 7 天',
  '30d': '近 30 天',
};

/**
 * Anonymous visitors page — every visitor who started the assessment at
 * /try/* before registering. Heart of the early-stage funnel. Click a
 * row to drill into their answers; the visitor → user conversion shows
 * inline as a chip.
 */
export function VisitorsPage(): JSX.Element {
  const [data, setData] = useState<VisitorListResponse | null>(null);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [recent, setRecent] = useState<RecentFilter>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { fmtDateTime } = useFmt();

  const load = useCallback(async (
    s: StatusFilter,
    r: RecentFilter,
    q: string,
    o: number,
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(o));
      if (s !== 'all') params.set('status', s);
      if (r !== 'all') params.set('recent', r);
      if (q !== '') params.set('search', q);
      const res = await request<VisitorListResponse>(
        `/admin/visitors?${params.toString()}`,
      );
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(status, recent, search, offset);
  }, [load, status, recent, search, offset]);

  function changeStatus(next: StatusFilter): void {
    setStatus(next);
    setOffset(0);
  }
  function changeRecent(next: RecentFilter): void {
    setRecent(next);
    setOffset(0);
  }
  function submitSearch(e: React.FormEvent): void {
    e.preventDefault();
    setSearch(searchInput.trim());
    setOffset(0);
  }

  const summary = data?.summary;
  const conversionRate =
    summary !== undefined && summary.total > 0
      ? Math.round((summary.converted / summary.total) * 100)
      : 0;

  return (
    <div className="space-y-5">
      <PageHeader
        title="访客"
        subtitle="未注册用户的测评数据 · 早期增长漏斗的核心"
      />

      {/* Summary metrics — show at a glance regardless of current filter */}
      {summary !== undefined && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard label="总访客" value={summary.total} tone="neutral" />
          <MetricCard label="近 7 天活跃" value={summary.last_7d} tone="info" />
          <MetricCard label="未转化" value={summary.unconverted} tone="warning" />
          <MetricCard
            label="转化率"
            value={`${conversionRate}%`}
            hint={`${summary.converted} 人已注册`}
            tone="success"
          />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1.5">
          {(['all', 'unconverted', 'converted'] as StatusFilter[]).map((s) => (
            <Chip key={s} active={status === s} onClick={(): void => changeStatus(s)}>
              {STATUS_LABEL[s]}
            </Chip>
          ))}
        </div>
        <span className="text-slate-700">·</span>
        <div className="flex gap-1.5">
          {(['all', '7d', '30d'] as RecentFilter[]).map((r) => (
            <Chip key={r} active={recent === r} onClick={(): void => changeRecent(r)}>
              {RECENT_LABEL[r]}
            </Chip>
          ))}
        </div>

        <form onSubmit={submitSearch} className="flex gap-2 flex-1 min-w-[240px]">
          <input
            type="text"
            value={searchInput}
            onChange={(e): void => setSearchInput(e.target.value)}
            placeholder="按 visitor_id / 国家 / 来源搜索"
            className="flex-1 h-9 px-3 bg-white/[0.03] border border-white/10 rounded-full text-sm text-slate-100 focus:outline-none focus:border-shark-500"
          />
          <GlassButton type="submit" size="sm" variant="primary">
            搜索
          </GlassButton>
        </form>
      </div>

      {error !== null && (
        <div className="rounded-lg bg-rose-900/30 border border-rose-700/50 px-3 py-2 text-sm text-rose-300">
          {error}
        </div>
      )}

      {/* List */}
      <div className="space-y-2">
        {loading && data === null ? (
          <LoadingState />
        ) : data === null || data.visitors.length === 0 ? (
          <EmptyState
            title={status === 'all' ? '还没有访客' : '当前筛选没有结果'}
            icon="👀"
          />
        ) : (
          data.visitors.map((v) => (
            <VisitorRow key={v.id} v={v} fmtDateTime={fmtDateTime} />
          ))
        )}
      </div>

      <Pagination
        offset={offset}
        total={data?.total ?? 0}
        pageSize={PAGE_SIZE}
        onChange={setOffset}
      />
    </div>
  );
}

function VisitorRow({
  v,
  fmtDateTime,
}: {
  v: VisitorListItem;
  fmtDateTime: (s: string | null | undefined) => string;
}): JSX.Element {
  const converted = v.converted_user_id !== null;
  return (
    <Link
      to={`/visitors/${v.id}`}
      className={`block rounded-lg border p-4 transition-colors hover:border-shark-500/50 ${
        converted
          ? 'border-emerald-800/40 bg-emerald-900/10'
          : 'border-white/10 bg-white/[0.04]'
      }`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap text-xs">
            <span className="font-mono text-slate-400">{v.id.slice(0, 8)}…</span>
            {converted ? (
              <span className="px-1.5 py-0.5 rounded border border-emerald-700/50 bg-emerald-900/30 text-emerald-200 text-[10px] uppercase tracking-wider">
                ✓ 已注册
              </span>
            ) : (
              <span className="px-1.5 py-0.5 rounded border border-amber-700/50 bg-amber-900/20 text-amber-200 text-[10px] uppercase tracking-wider">
                未转化
              </span>
            )}
            {v.country !== null && (
              <span className="px-1.5 py-0.5 rounded border border-white/10 bg-white/[0.04] text-slate-300 text-[10px]">
                {v.country}
              </span>
            )}
            {v.locale !== null && (
              <span className="text-slate-500">{v.locale}</span>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap text-sm">
            <span className="text-slate-200">
              答题 <span className="font-semibold text-shark-300">{v.attempts_count}</span> ·
              测评 <span className="font-semibold text-shark-300">{v.assessments_count}</span>
            </span>
            {converted && v.converted_user_email !== null && (
              <span className="text-slate-400">
                → <span className="text-emerald-300 font-medium">{v.converted_user_email}</span>
              </span>
            )}
          </div>
          {v.referrer !== null && (
            <p className="text-xs text-slate-500 mt-1.5 truncate">来源 · {v.referrer}</p>
          )}
        </div>
        <div className="text-right text-xs text-slate-400 shrink-0 space-y-0.5">
          <p>最近 · {fmtDateTime(v.last_seen_at)}</p>
          <p className="text-slate-600">首次 · {fmtDateTime(v.first_seen_at)}</p>
        </div>
      </div>
    </Link>
  );
}
